class ProductImages extends HTMLElement {
  constructor() {
    super();
    this.mainSwiper = null;
    this.thumbsSwiper = null;
    this.photoSwipeLightbox = null;
    this.desktopMediaQuery = window.matchMedia('(min-width: 768px)');
    this.onVariantChange = this.onVariantChange.bind(this);
    this.onBreakpointChange = this.onBreakpointChange.bind(this);
    this.onResize = this.onResize.bind(this);
  }

  connectedCallback() {
    this.initSwiper();
    this.initPhotoSwipe();
    document.addEventListener('variant:changed', this.onVariantChange);
    this.desktopMediaQuery.addEventListener('change', this.onBreakpointChange);
    window.addEventListener('resize', this.onResize);
  }

  disconnectedCallback() {
    this.destroySwipers();
    if (this.photoSwipeLightbox) {
      this.photoSwipeLightbox.destroy();
      this.photoSwipeLightbox = null;
    }
    document.removeEventListener('variant:changed', this.onVariantChange);
    this.desktopMediaQuery.removeEventListener(
      'change',
      this.onBreakpointChange,
    );
    window.removeEventListener('resize', this.onResize);
  }

  destroySwipers() {
    if (this.mainSwiper) {
      this.mainSwiper.destroy(true, true);
      this.mainSwiper = null;
    }

    if (this.thumbsSwiper) {
      this.thumbsSwiper.destroy(true, true);
      this.thumbsSwiper = null;
    }
  }

  async initPhotoSwipe() {
    if (!this.isConnected) return;
    if (!this.mainSwiper) return;
    if (this.dataset.disableLightbox === 'true') return;

    try {
      const PhotoSwipe = (await import(this.dataset.photoswipeUrl)).default;
      const PhotoSwipeLightbox = (
        await import(this.dataset.photoswipeLightboxUrl)
      ).default;

      const freshElement = this.isConnected
        ? this
        : document.querySelector('product-images');
      if (!freshElement) return;

      this.photoSwipeLightbox = new PhotoSwipeLightbox({
        gallery: freshElement,
        children: '.js-product-image-zoom',
        pswpModule: PhotoSwipe,
        initialZoomLevel: 'fit',
        secondaryZoomLevel: 2,
        maxZoomLevel: 4,
        loop: false,
      });

      this.photoSwipeLightbox.on('beforeOpen', () => {
        if (this.mainSwiper && this.photoSwipeLightbox.pswp) {
          this.photoSwipeLightbox.pswp.currIndex = this.mainSwiper.activeIndex;
        }
      });

      this.photoSwipeLightbox.on('change', () => {
        if (this.mainSwiper && this.photoSwipeLightbox.pswp) {
          this.mainSwiper.slideTo(this.photoSwipeLightbox.pswp.currIndex);
        }
      });

      this.photoSwipeLightbox.init();
    } catch (error) {
      console.error('PhotoSwipe initialization error:', error);
    }
  }

  syncThumbsHeight() {
    const thumbsContainer = this.querySelector('.product-images__thumbs');
    const mainSwiperContainer = this.querySelector('.js-product-main-swiper');

    if (!thumbsContainer) {
      return;
    }

    if (!this.desktopMediaQuery.matches || !mainSwiperContainer) {
      thumbsContainer.style.height = '';
      return;
    }

    const mainHeight = mainSwiperContainer.getBoundingClientRect().height;
    if (mainHeight > 0) {
      thumbsContainer.style.height = `${mainHeight}px`;
    }
  }

  initSwiper() {
    if (typeof Swiper === 'undefined') {
      console.warn('Swiper JS is not loaded yet.');
      return;
    }

    const mainSwiperContainer = this.querySelector('.js-product-main-swiper');
    if (!mainSwiperContainer) return;

    this.destroySwipers();

    const initialSlide = parseInt(this.dataset.initialSlide) || 0;
    const thumbsSwiperContainer = this.querySelector(
      '.js-product-thumbs-swiper',
    );
    const thumbsPrevButton = this.querySelector('.js-thumbs-prev');
    const thumbsNextButton = this.querySelector('.js-thumbs-next');
    const isDesktop = this.desktopMediaQuery.matches;

    if (thumbsSwiperContainer && isDesktop) {
      this.thumbsSwiper = new Swiper(thumbsSwiperContainer, {
        initialSlide: initialSlide,
        direction: 'vertical',
        slidesPerView: 'auto',
        spaceBetween: 4,
        freeMode: true,
        watchSlidesProgress: true,
        navigation: {
          prevEl: thumbsPrevButton,
          nextEl: thumbsNextButton,
        },
        breakpoints: {
          1024: {
            spaceBetween: 12,
          },
        },
      });
    }

    const mainSwiperConfig = {
      initialSlide: initialSlide,
      slidesPerView: 1,
      spaceBetween: 10,
      navigation: {
        nextEl: this.querySelector('.swiper-button-next'),
        prevEl: this.querySelector('.swiper-button-prev'),
      },
      pagination: {
        el: this.querySelector('.swiper-pagination'),
        type: 'progressbar',
      },
    };

    if (this.thumbsSwiper) {
      mainSwiperConfig.thumbs = {
        swiper: this.thumbsSwiper,
      };
    }

    this.mainSwiper = new Swiper(mainSwiperContainer, mainSwiperConfig);

    this.syncThumbsHeight();
    this.updateThumbNavigationVisibility();
    requestAnimationFrame(() => {
      this.syncThumbsHeight();
      this.updateThumbNavigationVisibility();
    });
  }

  updateThumbNavigationVisibility() {
    const thumbsPrevButton = this.querySelector('.js-thumbs-prev');
    const thumbsNextButton = this.querySelector('.js-thumbs-next');

    if (!thumbsPrevButton || !thumbsNextButton) {
      return;
    }

    if (!this.thumbsSwiper) {
      thumbsPrevButton.classList.add('hidden!');
      thumbsNextButton.classList.add('hidden!');
      return;
    }

    // Buttons start hidden in Liquid. Once Swiper navigation is initialized,
    // let Swiper's `swiper-button-disabled` class and CSS control visibility.
    thumbsPrevButton.classList.remove('hidden!');
    thumbsNextButton.classList.remove('hidden!');
  }

  onBreakpointChange() {
    this.initSwiper();
  }

  onResize() {
    this.syncThumbsHeight();

    if (this.mainSwiper) {
      this.mainSwiper.update();
    }

    if (this.thumbsSwiper) {
      this.thumbsSwiper.update();
    }

    this.updateThumbNavigationVisibility();
  }

  onVariantChange(event) {
    if (!this.mainSwiper) return;

    // Use querySelector to find the fresh element in the DOM after Idiomorph
    // because `this` might refer to a stale element that was swapped out.
    const freshElement = this.isConnected
      ? this
      : document.querySelector('product-images');
    if (!freshElement) return;

    const newSlideIndex = parseInt(freshElement.dataset.initialSlide) || 0;

    if (this.mainSwiper.activeIndex !== newSlideIndex) {
      this.mainSwiper.slideTo(newSlideIndex);
    }

    if (this.thumbsSwiper && this.thumbsSwiper.activeIndex !== newSlideIndex) {
      this.thumbsSwiper.slideTo(newSlideIndex);
    }

    this.syncThumbsHeight();
    this.updateThumbNavigationVisibility();
  }
}

if (!customElements.get('product-images')) {
  customElements.define('product-images', ProductImages);
}
